<template>
    <div v-loading="loading" class="user-portfolio-education">
        <div class="user-portfolio-education__head">

            <div class="user-portfolio-education__head-title">
                Образование
            </div>
            <div
                v-if="sortedEducations.length === 0"
                class="user-portfolio-education__head-experience user-portfolio-education__head-experience--not-filled"
            >
                <strong>Не указано</strong>
            </div>
        </div>

        <div
            v-if="sortedEducations.length"
            class="user-portfolio-education__education-places"
        >
            <div
                v-for="(education, index) in sortedEducations"
                :key="index"
                class="user-portfolio-education__education-place"
            >
                <div class="user-portfolio-education__education-place-date">
                    <div v-if="education.dateStart">{{ getDateString(education.dateStart) }}</div>

                    <div v-if="isMobile && education.dateStart && (education.isUntilNow || education.dateEnd)">-</div>

                    <div v-if="education.isNow">По настоящее время</div>
                    <div v-else-if="education.dateEnd">{{ getDateString(education.dateEnd) }}</div>

                    <div v-if="!education.isNow && !education.dateStart && !education.dateEnd">
                        Период не указан
                    </div>
                </div>
                <div class="user-portfolio-education__education-place-info">
                    <div class="user-portfolio-education__education-place-info-level">
                        {{ getLevelName(education.foreignLevelId) }}
                    </div>
                    <div
                        class="user-portfolio-education__education-place-info-place"
                        v-html="getHighlightHtml(
                            education.foreignPlaceId ? getPlaceName(education.foreignPlaceId) : education.educationPlace
                        )"
                    />
                    <div
                        class="user-portfolio-education__education-place-info-faculty"
                        v-html="getHighlightHtml(education.faculty)"
                    />
                </div>
                <div
                    class="user-portfolio-education__education-place-dot"
                    :class="{
                        'user-portfolio-education__education-place-dot--current': education.isNow,
                    }"
                ></div>
            </div>
        </div>

        <div
            v-if="getUser.userLanguages && getUser.userLanguages.length"
            class="user-portfolio-education__languages"
        >
            <div class="user-portfolio-education__languages-title">
                Знание языков:
            </div>
            <div class="user-portfolio-education__languages-list">
                <div
                    v-for="(lang, index) in getUser.userLanguages"
                    class="user-portfolio-education__languages-list-item"
                    :key="index"
                >
                    <div class="user-portfolio-education__languages-list-item-name">
                        {{ getLangName(lang.foreignLanguageId) }}
                    </div>
                    <div class="user-portfolio-education__languages-list-item-level">
                        {{ getLangLevelName(lang.foreignLanguageLevelId) }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import moment from 'moment';
import {getHighlightGroupsKeywordsSet, highlightStrKeywords} from "@/tools/graphQueryHelpers";

export default {
    props: {
        highlights: {
            type: Object,
            required: false,
            default: () => null,
        },
    },

    data: () => ({
        loading: false,
    }),

    async created() {
        this.loading = true;

        await Promise.all([
            this.loadRegistryItems({ type: 'education_level', withDeleted: true }),
            this.loadRegistryItems({ type: 'languages', withDeleted: true }),
            this.loadRegistryItems({ type: 'level_language', withDeleted: true }),
            this.loadUserEducations(this.getUser.userId),
        ]);
        await this.loadEducationPlaceNames();

        this.loading = false;
    },

    watch: {
        async 'getUser.userId'(userId) {
            this.loading = true;

            await this.loadUserEducations(userId);
            await this.loadEducationPlaceNames();

            this.loading = false;
        },
    },

    computed: {
        ...mapGetters([
            'isMobile',
        ]),
        ...mapGetters('newUsers', [
            'getUser',
            'getUserEducations',
        ]),
        ...mapGetters('data', [
            'getRegistryItems',
            'getRegistryItemById',
        ]),

        highlightKeywords() {
            return getHighlightGroupsKeywordsSet(this.highlights, [
                'education.faculty',
                'education.education_place',
                'education.foreign_place',
                'education.specialty_special',
            ]);
        },

        sortedEducations() {
            return (this.getUserEducations || [])
                .sort((a, b) => {
                    if ((a.isNow || b.isNow) && !(a.isNow && b.isNow)) {
                        return a.isNow ? -1 : 1;
                    }

                    if ((!a.dateStart || !b.dateStart) && !(!a.dateStart && !b.dateStart)) {
                        return a.dateStart ? -1 : 1;
                    }

                    const startDiff = moment(a.dateStart || new Date()).diff(b.dateStart || new Date());

                    if (startDiff !== 0) {
                        return startDiff > 0 ? -1 : 1;
                    }

                    if (!a.dateEnd || !b.dateEnd) {
                        return !a.dateEnd && !b.dateEnd ? 0 : a.dateEnd ? -1 : 1;
                    }

                    const endDiff = moment(a.dateEnd || new Date()).diff(b.dateEnd || new Date());

                    return endDiff === 0 ? 0 : endDiff > 0 ? -1 : 1;
                })
            ;
        },

        getLevelName() {
            return (id) => {
                const level = this.getRegistryItems('education_level').find(v => v.id === id);
                return level ? level.field_value : '';
            };
        },

        getPlaceName() {
            return (id) => {
                const place = this.getRegistryItems('educational_institutions').find(v => v.id === id);
                return place ? place.field_value : '';
            };
        },
    },

    methods: {
        ...mapActions('newUsers', [
            'loadUserEducations',
        ]),
        ...mapActions('data', [
            'loadRegistryItems',
            'loadRegistryItemsByIDs',
        ]),

        getDateString(date) {
            const dateStr = moment(date).format('MMMM YYYY');
            return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
        },

        async loadEducationPlaceNames() {
            const places = this.getUserEducations.map(v => v.foreignPlaceId);

            if (!places.length) {
                return;
            }

            this.loadRegistryItemsByIDs({ type: 'educational_institutions', ids: places });
        },

        getLangName(id) {
            const lang = this.getRegistryItems('languages').find(v => v.id === id);
            return lang ? lang.field_value : '';
        },

        getLangLevelName(id) {
            const level = this.getRegistryItems('level_language').find(v => v.id === id);
            return level ? level.field_value : '';
        },

        getHighlightHtml(str) {
            return str ? highlightStrKeywords(str, this.highlightKeywords) : str;
        },
    },
};

</script>

<style scoped lang="scss">
@import "~@/styles/rem";
@import '~@/styles/variables';

.user-portfolio-education {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: stretch;
    align-items: flex-start;
    gap: mrem(40);

    @include on-desktop {
        gap: rem(40);
    }


    &__head {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        @include on-desktop {
            flex-direction: row;
            align-items: center;
        }

        &-title {
            color: $color-main-blue;
            font-weight: 700;
            font-size: mrem(21);
            line-height: mrem(30);

            @include on-desktop {
                font-size: rem(21);
                line-height: rem(30);
            }
        }

        &-experience {
            display: flex;
            flex-direction: row;
            align-items: center;
            font-weight: 400;
            gap: mrem(6);

            @include on-desktop {
                gap: rem(6);
            }

            strong {
                font-weight: 600;
                font-size: mrem(18);
                line-height: mrem(24);

                @include on-desktop {
                    font-size: rem(18);
                    line-height: rem(24);
                }
            }

            &--empty, &--not-filled {
                color: $color-main-grey;
                font-weight: 500;
                font-size: rem(14);
                line-height: rem(20);

                strong {
                    font-weight: inherit;
                }
            }
        }
    }

    &__education-places {
        display: flex;
        flex-direction: column;
        gap: mrem(40);

        @include on-desktop {
            gap: rem(40);
        }

        .user-portfolio-education__education-place {
            display: grid;
            position: relative;
            grid-template-columns: auto;
            gap: mrem(4);
            padding-left: mrem(22);

            @include on-desktop {
                grid-template-columns: rem(180) auto;
                gap: rem(24);
                padding-left: rem(22);
            }

            &-dot {
                position: absolute;
                z-index: 2;
                border-radius: 100%;
                left: 0;
                background: url("~@/assets/images/portfolio/dot-list.svg") no-repeat center center;
                background-size: contain;
                background-color: #fff;
                width: mrem(16);
                height: mrem(16);
                top: mrem(2);

                @include on-desktop {
                    width: rem(16);
                    height: rem(16);
                    top: rem(2);
                }

                &--current {
                    background-image: url("~@/assets/images/portfolio/dot-list--active.svg");
                }
            }

            &:after {
                z-index: 1;
                content: '';
                position: absolute;
                background: $color-main-blue;
                width: mrem(1);
                height: calc(100% + #{mrem(45)});
                left: mrem(7.5);
                top: mrem(8);

                @include on-desktop {
                    width: rem(1);
                    height: calc(100% + #{rem(45)});
                    left: rem(7.5);
                    top: rem(8);
                }
            }

            &:last-of-type {
                &:after {
                    display: none;
                }
            }

            &-date {
                display: flex;
                color: $color-main-grey;
                font-weight: 600;
                gap: mrem(4);
                font-size: mrem(14);
                line-height: mrem(20);

                @include on-desktop {
                    display: block;
                    font-size: rem(14);
                    line-height: rem(20);
                }
            }

            &-info {
                display: flex;
                flex-direction: column;
                gap: mrem(6);

                @include on-desktop {
                    gap: rem(6);
                }

                &-level {
                    color: $color-main-blue;
                    font-weight: 400;
                    font-size: mrem(14);
                    line-height: mrem(20);

                    @include on-desktop {
                        font-size: rem(14);
                        line-height: rem(20);
                    }
                }
                &-place {
                    font-weight: 700;
                }
                &-faculty {
                    font-weight: 400;
                    font-size: mrem(14);
                    line-height: mrem(20);

                    @include on-desktop {
                        font-size: rem(14);
                        line-height: rem(20);
                    }
                }
            }
        }
    }

    &__languages {
        display: flex;
        flex-direction: column;
        border: none;
        background: $color-main-notice-lighter;
        cursor: pointer;
        font-weight: 400;
        width: calc(100% + #{mrem(40)});
        padding: mrem(20) mrem(40);
        gap: mrem(16);
        border-radius: 0 0 mrem(16) mrem(16);
        margin: mrem(-20);

        @include on-desktop {
            width: calc(100% + #{rem(40)});
            padding: rem(20) rem(40);
            gap: rem(16);
            border-radius: 0 0 rem(16) rem(16);
            margin: rem(-20);
        }

        &-title {
            color: $color-main-grey;
            font-weight: 600;
            font-size: mrem(14);
            line-height: mrem(20);

            @include on-desktop {
                font-size: rem(14);
                line-height: rem(20);
            }
        }

        &-list {
            display: flex;
            flex-direction: column;
            gap: mrem(6);

            @include on-desktop {
                gap: rem(6);
            }

            &-item {
                display: flex;
                justify-content: space-between;
                gap: mrem(16);

                @include on-desktop {
                    display: grid;
                    justify-content: flex-start;
                    grid-template-columns: rem(180) auto;
                    gap: rem(16);
                }

                &-name {
                    font-weight: 400;
                    font-size: mrem(14);
                    line-height: mrem(20);

                    @include on-desktop {
                        font-size: rem(14);
                        line-height: rem(20);
                    }
                }
                &-level {
                    color: $color-main-blue;
                    font-weight: 600;
                    font-size: mrem(14);
                    line-height: mrem(20);

                    @include on-desktop {
                        font-size: rem(14);
                        line-height: rem(20);
                    }
                }
            }
        }
    }
}

</style>
